// TODO: Review AI overlay dark mode contrast ratios with UX - https://taskei.amazon.dev/tasks/NHE-5673
import "./AIAutomationOverlay.scss";

import { Button } from "@amzn/stencil-react-components/button";
import { Card } from "@amzn/stencil-react-components/card";
import {
  Chip,
  ChipBackgroundColor,
  ChipSize,
} from "@amzn/stencil-react-components/chip";
import { ThemeMode } from "@amzn/stencil-react-components/context";
import { Col, Row, View } from "@amzn/stencil-react-components/layout";
import { Text } from "@amzn/stencil-react-components/text";
import { IconAlertCircleSmall, IconPerson } from "@amzn/stencil-react-icons";
import { useContext, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { useThemeMode } from "@/stores/contexts/ThemeModeContext";

import aiUserInputIcon from "../../assets/icons/aiUserInput.svg";
import aivaIconSmall from "../../assets/icons/aivaIconSmall.svg";
import aivaIconSmallLight from "../../assets/icons/aivaIconSmall-light.svg";
import aivaIcon from "../../assets/images/aivaIcon.svg";
import { getOS, OPERATING_SYSTEM } from "../../helpers/os-helper";
import { AppContext } from "../../stores/appStore";
import TypingIndicator from "../Commons/TypingIndicator";
import { MANUAL_FALLBACK_THRESHOLD } from "../constants";

// Constants for card width
const CARD_WIDTH = 700;
const CARD_WIDTH_USER_TURN = 950;

interface AIAutomationOverlayProps {
  onStopAutomation: () => void;
  isFailed?: boolean;
  failureCount?: number;
  onManualFallback?: () => void;
}

/**
 * Sub-component that displays VPN username and PIN instructions for PENDING_USER_ACTION state.
 * Shows numbered steps and warning with alert icon.
 */
const VpnUsernamePinInstructions = ({
  username,
}: {
  username?: string;
}): JSX.Element => (
  <Col gridGap="S200">
    {/* Hardcoded dark text — this renders inside the user-turn card which has a static light gradient. */}
    <Text style={{ color: "#232F3E" }} fontSize="T200" className="ai-text-wrap">
      <ol className="ai-prompt-steps">
        <li>
          <FormattedMessage
            id={
              username
                ? "connectVpn-ai-automation-prompt-step1-with-username"
                : "connectVpn-ai-automation-prompt-step1"
            }
            values={username ? { username } : undefined}
          />
        </li>
        <li>
          <FormattedMessage id="connectVpn-ai-automation-prompt-step2" />
        </li>
      </ol>
    </Text>
    <Row gridGap="S200" alignItems="flex-start">
      <IconAlertCircleSmall
        style={{ color: "#232F3E" }}
        margin={{ top: "dimensions.spacing.100" }}
        aria-hidden={true}
      />
      <Text
        // TODO: Review AI disclosure dark mode styling with UX - https://taskei.amazon.dev/tasks/NHE-5673
        style={{ color: "#232F3E" }}
        fontSize="T200"
        className="ai-text-wrap"
        dataTestId="promptWarningText"
      >
        <FormattedMessage id="connectVpn-ai-automation-prompt-warning" />
      </Text>
    </Row>
  </Col>
);

/**
 * Sub-component that displays the chip, spinner, and overlay during automation.
 * Always visible to maintain visual context, even when automation has failed.
 */
const AIAutomationActiveState = ({
  animate,
  isUserTurn,
  isFailed,
  themeMode,
}: {
  animate: boolean;
  isUserTurn: boolean;
  isFailed: boolean;
  themeMode: ThemeMode;
}): JSX.Element => (
  <>
    <Chip
      size={ChipSize.Small}
      backgroundColor={
        isUserTurn ? ChipBackgroundColor.Green : ChipBackgroundColor.Purple
      }
      dataTestId="aiAutomationChip"
      id="ai-automation-chip"
      icon={
        isUserTurn ? (
          <IconPerson color="color.icon.primary-default" aria-hidden={true} />
        ) : (
          <img
            src={
              themeMode === ThemeMode.Dark ? aivaIconSmallLight : aivaIconSmall
            }
            alt=""
            className="ai-icon-small"
          />
        )
      }
    >
      <FormattedMessage
        id={
          isUserTurn
            ? "connectVpn-ai-automation-user-turn-notice"
            : "connectVpn-ai-automation-in-progress"
        }
      />
    </Chip>
    {!isUserTurn && !isFailed && (
      <View className="ai-automation-spinner" dataTestId="aiAutomationSpinner">
        <TypingIndicator size="md" color="dark" />
      </View>
    )}
    <View
      className={`ai-automation-overlay ${animate ? "animate" : ""} ${
        isUserTurn ? "user-turn" : ""
      }`}
    />
  </>
);

/**
 * Component that displays during AI automation workflows.
 * Shows real-time agent progress updates and provides a stop button.
 * When isFailed is true, shows error state with "Back to set up" button.
 * When isUserTurn is true (PENDING_USER_ACTION), shows VPN username/PIN instructions.
 */
export default function AIAutomationOverlay({
  onStopAutomation,
  isFailed = false,
  failureCount = 0,
  onManualFallback,
}: AIAutomationOverlayProps): JSX.Element {
  const { state } = useContext(AppContext);
  const { themeMode } = useThemeMode();
  const intl = useIntl();
  const [animate, setAnimate] = useState(false);
  const [agentStatus, setAgentStatus] = useState<string>(
    intl.formatMessage({ id: "connectVpn-ai-automation-cursor-warning" })
  );
  const [isUserTurn, setIsUserTurn] = useState(false);
  const [isProcessing, setIsProcessing] = useState(!isFailed);

  useEffect(() => {
    if (isFailed) {
      setAgentStatus(
        intl.formatMessage({ id: "connectVpn-ai-automation-failed-message" })
      );
      setIsProcessing(false);
      setIsUserTurn(false);
    }
  }, [isFailed, intl]);

  useEffect(() => {
    setAnimate(true);

    // Don't listen for updates when in failed state
    if (isFailed) {
      return;
    }

    const cleanup = window.electronAPI?.onAgentActionProgress?.((result) => {
      // Only process successful agent responses
      if (result.success && result.response) {
        const response = result.response;

        if (response.message) {
          setAgentStatus(response.message);
        }
        // Check if user needs to take action
        const isTerminal = ["COMPLETED", "FAILED", "TIMEOUT"].includes(
          response.state
        );

        if (response.state === "PENDING_USER_ACTION") {
          setIsUserTurn(true);
          setIsProcessing(false);
        } else if (isTerminal) {
          setIsUserTurn(false);
          setIsProcessing(false);
        } else {
          // Non-terminal, non-user-action: show loading dots while agent works
          setIsUserTurn(false);
          setIsProcessing(true);
        }
      }
    });

    return () => {
      cleanup?.();
    };
  }, [isFailed]);

  const handleStopClick = () => {
    // After multiple failures (at threshold or more), go directly to manual instructions
    if (
      isFailed &&
      failureCount >= MANUAL_FALLBACK_THRESHOLD &&
      onManualFallback
    ) {
      onManualFallback();
    } else {
      onStopAutomation();
    }
  };

  // Helper function to determine the title message ID based on state
  const getTitleMessageId = (): string => {
    if (isFailed) {
      return failureCount >= MANUAL_FALLBACK_THRESHOLD
        ? "connectVpn-ai-automation-unableToComplete-title"
        : "connectVpn-ai-automation-failed-notice";
    }
    if (isUserTurn) return "connectVpn-ai-automation-user-turn-notice";
    return "connectVpn-ai-automation-screen-control-notice";
  };

  // Helper function to determine the button text message ID
  const getButtonTextId = (): string => {
    if (isFailed) {
      return failureCount >= MANUAL_FALLBACK_THRESHOLD
        ? "connectVpn-ai-disclosure-decline-button"
        : "connectVpn-ai-automation-back-to-setup-button";
    }
    return "connectVpn-ai-automation-stop-button";
  };

  const osType = getOS();
  const cardClassName = `ai-automation-card ${animate ? "animate" : ""} ${
    osType === OPERATING_SYSTEM.WINDOWS ? "windows-position" : "macos-position"
  }${isUserTurn ? " user-turn-active" : ""}`;

  return (
    <Col
      gridGap="S400"
      dataTestId="aiAutomationOverlay"
      className="ai-automation-container"
    >
      <AIAutomationActiveState
        animate={animate}
        isUserTurn={isUserTurn}
        isFailed={isFailed}
        themeMode={themeMode}
      />

      <Card
        className={cardClassName}
        padding={{ top: "S400", bottom: "S400", left: "S200", right: "S200" }}
        width={isUserTurn ? CARD_WIDTH_USER_TURN : CARD_WIDTH}
        dataTestId="aiAutomationCard"
      >
        <Row
          className="ai-button-container"
          justifyContent="space-between"
          alignItems="center"
          gridGap="S200"
        >
          <Row gridGap="S200" alignItems="flex-start" flex={1}>
            <img
              src={isUserTurn ? aiUserInputIcon : aivaIcon}
              alt={isUserTurn ? "VPN" : "Aiva"}
              className={`ai-icon-large${
                isUserTurn ? " ai-icon-centered" : ""
              }`}
            />
            <Col
              gridGap="S100"
              padding={{
                top: "dimensions.spacing.200",
                left: "dimensions.spacing.250",
              }}
              flex={1}
            >
              <Row alignItems="flex-end" gridGap="S100">
                <Text
                  fontWeight="bold"
                  fontSize="T300"
                  // Hardcoded colors — dark gradient card uses white, light user-turn card uses dark navy.
                  style={{ color: isUserTurn ? "#232F3E" : "white" }}
                >
                  <FormattedMessage id={getTitleMessageId()} />
                </Text>
                {isProcessing && !isUserTurn && !isFailed && (
                  <TypingIndicator size="sm" />
                )}
              </Row>
              {isUserTurn && !isFailed ? (
                <VpnUsernamePinInstructions username={state.amazonUsername} />
              ) : (
                <Text
                  color={undefined}
                  // Hardcoded white — dark gradient card background is static regardless of theme mode.
                  style={{ color: "white" }}
                  fontSize="T200"
                  className="ai-text-wrap"
                  dataTestId="agentStatusMessage"
                >
                  {isFailed && failureCount >= MANUAL_FALLBACK_THRESHOLD ? (
                    <FormattedMessage id="connectVpn-ai-automation-unableToComplete-description" />
                  ) : (
                    agentStatus
                  )}
                </Text>
              )}
            </Col>
          </Row>
          <Button onClick={handleStopClick} dataTestId="aiStopButton">
            <FormattedMessage id={getButtonTextId()} />
          </Button>
        </Row>
      </Card>
    </Col>
  );
}
